<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <!-- 検索条件 -->
          <v-row>
            <v-col cols="3">
              <v-text-field
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.waybillNo"
                :label="$t('okurijo.label.denpyouBangou')"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.shukkasaki"
                :items="shukkasakiItem"
                item-title="code_name"
                item-value="code"
                :label="$t('okurijo.label.shukkasaki')"
                :clearable="true"
                return-object
              />
            </v-col>
            <v-col cols="3">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.carrier"
                :items="carrierItem"
                item-title="name"
                item-value="value"
                :label="$t('okurijo.label.carrier')"
                :clearable="true"
                return-object
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="3">
              <DatePicker
                ref="shukkaFrom"
                v-model="searchCondition.shipDateFrom"
                :label="$t('okurijo.label.shukkaDateFrom')"
              ></DatePicker>
            </v-col>
            <v-col cols="3">
              <DatePicker
                ref="shukkaTo"
                v-model="searchCondition.shipDateTo"
                :label="$t('okurijo.label.shukkaDateTo')"
              ></DatePicker>
            </v-col>
            <v-col cols="6" class="text-right">
              <v-btn text @click="clear">{{ $t('common.action.clear') }}</v-btn>
              <v-btn
                theme="dark"
                class="ml-4 px-8 bg-grey-darken-3"
                elevation="2"
                @click.prevent="search"
                :loading="isSearching"
                >{{ $t('common.action.search') }}</v-btn
              >
            </v-col>
          </v-row>

          <!-- 一覧 -->
          <v-row>
            <v-col cols="12">
              <v-table density="compact" class="text-no-wrap">
                <thead>
                  <tr>
                    <th
                      v-for="header in headers"
                      :key="header"
                      @click.stop="tableSort(header)"
                      :style="header === 'boxLink' ? '' : 'cursor: pointer;'"
                    >
                      {{ $t("okurijo.label.table." + header) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="data in filteredDataList" :key="data.no">
                    <template v-for="header in headers" :key="header">
                      <!-- 配送業者バッジ -->
                      <td v-if="header === 'carrier'">
                        <v-chip
                          size="small"
                          label
                          class="justify-center"
                          style="min-width: 64px;"
                          :color="data.carrier === $t('okurijo.carrier.yamato') ? 'indigo' : 'deep-orange'"
                          variant="tonal"
                        >{{ data.carrier }}</v-chip>
                      </td>
                      <!-- 箱を見る（リンクのみ・詳細画面は無し） -->
                      <td v-else-if="header === 'boxLink'" class="text-right">
                        <a
                          class="text-primary"
                          style="cursor: pointer; text-decoration: none;"
                          :title="$t('okurijo.label.boxLink')"
                          @click.stop="openBox(data)"
                        >{{ $t('okurijo.label.boxLink') }}</a>
                      </td>
                      <!-- 数値は右寄せ -->
                      <td v-else-if="header === 'boxCount' || header === 'totalQty'" class="text-right">
                        {{ data[header] }}
                      </td>
                      <td v-else>
                        {{ data[header] }}
                      </td>
                    </template>
                  </tr>
                  <tr v-if="filteredDataList.length === 0">
                    <td :colspan="headers.length" class="text-center text-medium-emphasis">
                      {{ $t('common.label.all') }} 0 {{ $t('common.label.totalCountOf') }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <!-- ページング -->
          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination v-model="currentPageNo" :length="maxPageNo" />
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    v-model="displayPerPage"
                    :items="displayPerPageItem"
                    item-title="name"
                    item-value="value"
                    :label="$t('common.label.displayCountPerPage')"
                    @update:modelValue="resetCondition()"
                    return-object
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  {{ displayContentNo.minNo }} - {{ displayContentNo.maxNo }}{{ $t('common.label.displayedDataRange') }}
                  / {{ dataList.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn
                dark
                class="px-8 bg-indigo-darken-1"
                elevation="2"
                @click="downloadCsv"
                :disabled="dataList.length == 0"
                >{{ $t('common.action.outputCsv') }}</v-btn
              >
            </v-col>
          </v-row>
        </v-container>

        <!-- 箱明細モーダル（箱の中身） -->
        <v-dialog v-model="boxDialog" width="900" scrollable>
          <v-card v-if="selectedWaybill">
            <v-card-title class="d-flex align-center">
              <span>{{ $t('okurijo.modal.title') }}</span>
              <v-spacer></v-spacer>
              <v-btn icon variant="text" density="comfortable" @click="boxDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <!-- 伝票情報 -->
              <v-row dense class="mb-2">
                <v-col cols="3">
                  <div class="text-caption text-medium-emphasis">{{ $t('okurijo.label.table.waybillNo') }}</div>
                  <div class="font-weight-bold text-primary">{{ selectedWaybill.waybillNo }}</div>
                </v-col>
                <v-col cols="3">
                  <div class="text-caption text-medium-emphasis">{{ $t('okurijo.label.table.store') }}</div>
                  <div class="font-weight-medium">{{ selectedWaybill.store }}</div>
                </v-col>
                <v-col cols="2">
                  <div class="text-caption text-medium-emphasis">{{ $t('okurijo.label.table.shipDate') }}</div>
                  <div>{{ selectedWaybill.shipDate }}</div>
                </v-col>
                <v-col cols="2">
                  <div class="text-caption text-medium-emphasis">{{ $t('okurijo.label.table.carrier') }}</div>
                  <v-chip
                    size="small"
                    label
                    class="justify-center"
                    style="min-width: 64px;"
                    :color="selectedWaybill.carrier === $t('okurijo.carrier.yamato') ? 'indigo' : 'deep-orange'"
                    variant="tonal"
                  >{{ selectedWaybill.carrier }}</v-chip>
                </v-col>
                <v-col cols="2" class="text-right">
                  <div class="text-caption text-medium-emphasis">{{ $t('okurijo.modal.waybillTotal') }}</div>
                  <div class="font-weight-bold">
                    {{ selectedWaybill.boxCount }}{{ $t('okurijo.modal.boxUnit') }} / {{ selectedWaybill.totalQty }}{{ $t('okurijo.modal.pieceUnit') }}
                  </div>
                </v-col>
              </v-row>
              <v-divider class="mb-3"></v-divider>
              <v-row>
                <!-- 箱を選択 -->
                <v-col cols="4">
                  <div class="text-caption text-medium-emphasis mb-2">{{ $t('okurijo.modal.selectBox') }}</div>
                  <v-list density="compact" class="py-0">
                    <v-list-item
                      v-for="(b, idx) in selectedWaybill.boxes"
                      :key="b.seq"
                      :active="idx === selectedBoxIdx"
                      border
                      rounded="lg"
                      class="mb-2"
                      @click="selectBox(idx)"
                    >
                      <div class="d-flex justify-space-between align-center">
                        <span class="font-weight-bold">{{ b.boxId }}</span>
                        <v-chip size="x-small" label>{{ b.seq }}/{{ b.boxTotal }}{{ $t('okurijo.modal.boxNoSuffix') }}</v-chip>
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        {{ $t('okurijo.modal.total') }} {{ b.qty }}{{ $t('okurijo.modal.pieceUnit') }} ・ {{ b.items.length }}{{ $t('okurijo.modal.itemCountSuffix') }}
                      </div>
                    </v-list-item>
                  </v-list>
                </v-col>
                <!-- 箱の中身 -->
                <v-col cols="8">
                  <div class="text-subtitle-2 mb-2" v-if="selectedBox">
                    {{ selectedBox.boxId }}（{{ selectedBox.seq }}/{{ selectedBox.boxTotal }}{{ $t('okurijo.modal.boxNoSuffix') }}・{{ $t('okurijo.modal.total') }} {{ selectedBox.qty }}{{ $t('okurijo.modal.pieceUnit') }}）
                  </div>
                  <v-table density="compact" class="text-no-wrap">
                    <thead>
                      <tr>
                        <th>{{ $t('okurijo.modal.janCode') }}</th>
                        <th>{{ $t('okurijo.modal.productName') }}</th>
                        <th class="text-right">{{ $t('okurijo.modal.qty') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(it, ix) in (selectedBox ? selectedBox.items : [])" :key="ix">
                        <td>{{ it.jan }}</td>
                        <td>{{ it.name }}</td>
                        <td class="text-right">{{ it.qty }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colspan="2" class="font-weight-bold">{{ $t('okurijo.modal.total') }}</td>
                        <td class="text-right font-weight-bold text-primary">{{ selectedBox ? selectedBox.qty : 0 }}</td>
                      </tr>
                    </tfoot>
                  </v-table>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn color="indigo darken-1" dark @click="boxDialog = false">{{ $t('okurijo.modal.close') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import DatePicker from "@/components/DatePicker";
import api from "@/apis/staff";
import utils from "@/utils";

export default {
  components: { AppWrapperView, DatePicker },
  data: () => ({
    currentPageNo: 1,
    displayPerPage: {},
    displayPerPageItem: [],
    sortState: { target: "shipDate", ascent: false }, // 既定: 出荷日の新しい順

    shukkasakiItem: [],
    carrierItem: [],

    dataList: [],
    searchCondition: {
      waybillNo: "",
      shukkasaki: null,
      carrier: null,
      shipDateFrom: "",
      shipDateTo: "",
    },
    isSearching: false,

    // 箱明細モーダル
    boxDialog: false,
    selectedWaybill: null,
    selectedBoxIdx: 0,
  }),
  computed: {
    // モーダルで選択中の箱
    selectedBox() {
      const boxes = this.selectedWaybill?.boxes ?? [];
      if (boxes.length === 0) return null;
      const idx = Math.min(this.selectedBoxIdx, boxes.length - 1);
      return boxes[idx];
    },
    // 一覧のヘッダー（列順）
    headers() {
      return ["no", "waybillNo", "store", "shipDate", "deliveryDate", "carrier", "boxCount", "totalQty", "boxLink"];
    },
    displayContentNo() {
      const minNo = this.dataList.length != 0 ? 1 + (this.displayPerPage.value * (this.currentPageNo - 1)) : 0;
      const maxNo = this.displayPerPage.value * this.currentPageNo;
      if (maxNo > this.dataList.length || this.displayPerPage.value == 0) {
        return { minNo: minNo, maxNo: this.dataList.length };
      }
      return { minNo: minNo, maxNo: maxNo };
    },
    maxPageNo() {
      if (this.displayPerPage.value == 0) return 0;
      return Math.ceil(this.dataList.length / this.displayPerPage.value);
    },
    sortedTable() {
      let tempArray = this.dataList.map((x) => x);
      if (this.sortState.target != "" && this.sortState.target != "boxLink") {
        const t = this.sortState.target;
        tempArray = tempArray.sort((a, b) => {
          if (a[t] > b[t]) return this.sortState.ascent ? 1 : -1;
          if (a[t] < b[t]) return this.sortState.ascent ? -1 : 1;
          return 0;
        });
      }
      tempArray = tempArray.map((x, index) => {
        x.no = index + 1;
        return x;
      });
      return tempArray;
    },
    filteredDataList() {
      return this.sortedTable.slice(this.displayContentNo.minNo - 1, this.displayContentNo.maxNo);
    },
  },
  methods: {
    resetCondition() {
      this.currentPageNo = 1;
      this.sortState.target = "shipDate"; // 既定: 出荷日
      this.sortState.ascent = false;      // 新しい順（降順）
    },
    tableSort(header) {
      if (header == "no" || header == "boxLink") return;
      this.currentPageNo = 1;
      if (this.sortState.target == header) {
        this.sortState.ascent = !this.sortState.ascent;
      } else {
        this.sortState.target = header;
        this.sortState.ascent = true;
      }
    },
    // 出荷先店舗リスト取得（他ページと同じ /store/search を利用）
    // selectSiteFlag: 1 = 自店舗のみ（ログインユーザの権限店舗）
    getShopList() {
      api.post("/store/search", { selectSiteFlag: 1 })
        .then((response) => {
          if (response.data?.status == 1) return;
          (response.data.results?.shopList ?? []).forEach((e) => {
            this.shukkasakiItem.push({ name: e.name, code: e.code, code_name: e.code + "_" + e.name });
          });
        })
        .catch(() => {
          // 店舗取得失敗時は空のままにする
        });
    },
    clear() {
      this.$refs.shukkaTo.clear();
      this.$refs.shukkaFrom.clear();
      this.searchCondition = {
        waybillNo: "",
        shukkasaki: null,
        carrier: null,
        shipDateFrom: "",
        shipDateTo: "",
      };
    },
    search() {
      this.isSearching = true;
      const c = this.searchCondition;
      const params = {
        waybillNo: c.waybillNo || null,
        shukkasakiCode: c.shukkasaki?.code || null,
        carrierType: c.carrier?.value ?? null,
        shipDateFrom: c.shipDateFrom || null,
        shipDateTo: c.shipDateTo || null,
      };
      api.post("/okurijo/search", params)
        .then((response) => {
          if (response.data?.status == 1) {
            this.dataList = [];
            return;
          }
          const list = response.data.results?.waybillList ?? [];
          // 配送業者区分（1:ヤマト 2:佐川）を表示用ラベルに変換
          this.dataList = list.map((r) => ({
            ...r,
            carrier: this.carrierLabel(r.carrierType),
          }));
          this.resetCondition();
        })
        .catch(() => {
          this.dataList = [];
        })
        .finally(() => {
          this.isSearching = false;
        });
    },
    carrierLabel(carrierType) {
      if (carrierType === 2) return this.$t("okurijo.carrier.sagawa");
      return this.$t("okurijo.carrier.yamato");
    },
    // 箱を見る（箱明細モーダルを開く）
    openBox(data) {
      this.selectedWaybill = data;
      this.selectedBoxIdx = 0;
      this.boxDialog = true;
    },
    selectBox(idx) {
      this.selectedBoxIdx = idx;
    },
    downloadCsv() {
      const csvHeaders = this.headers.filter((h) => h !== "boxLink");
      utils.simpleDownloadCsv(csvHeaders, this.sortedTable, "okurijo.label.table.", "okurijo.fileNamePrefix.csv", this.$t);
    },
  },
  mounted() {
    this.displayPerPage = utils.getDisplayPerPage(this.$t);
    this.displayPerPageItem = utils.getDisplayPerPageItem(this.$t);
    this.carrierItem = [
      { name: this.$t("okurijo.carrier.yamato"), value: 1 },
      { name: this.$t("okurijo.carrier.sagawa"), value: 2 },
    ];
    this.getShopList();
  },
};
</script>
